import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Observable } from 'rxjs';

/**
 * Translation service that wraps the TranslocoService to provide
 * centralized translation functionality for the application.
 */
@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    constructor(private translocoService: TranslocoService) {}

    /**
     * Translates a key
     * @param key The key to translate
     * @param params Optional parameters for the translation
     * @returns The translated string
     */
    translate(key: string, params?: Record<string, any>): string {
        return this.translocoService.translate(key, params);
    }

    /**
     * Gets the active language
     * @returns The current active language
     */
    getActiveLang(): string {
        return this.translocoService.getActiveLang();
    }

    /**
     * Gets the default language
     * @returns The default language
     */
    getDefaultLang(): string {
        // Default implementation returns the first available language or 'en' if none available
        const availableLangs = this.getAvailableLangs();
        if (Array.isArray(availableLangs) && availableLangs.length > 0) {
            if (typeof availableLangs[0] === 'string') {
                return availableLangs[0] as string;
            } else if (availableLangs[0] && 'id' in availableLangs[0]) {
                return (availableLangs[0] as { id: string }).id;
            }
        }
        return 'en';
    }

    /**
     * Sets the active language
     * @param lang The language to set as active
     */
    setActiveLang(lang: string): void {
        this.translocoService.setActiveLang(lang);
    }

    /**
     * Gets the list of available languages
     * @returns The list of available languages
     */
    getAvailableLangs(): { id: string; label: string }[] | string[] {
        return this.translocoService.getAvailableLangs();
    }

    /**
     * Gets an observable of the active language changes
     * @returns An observable of the active language
     */
    langChanges$(): Observable<string> {
        return this.translocoService.langChanges$;
    }

    /**
     * Gets an observable of translations for a specific key
     * @param key The key to get translations for
     * @param params Optional parameters for the translation
     * @returns An observable of the translated string
     */
    selectTranslate(key: string, params?: Record<string, any>): Observable<string> {
        return this.translocoService.selectTranslate(key, params);
    }

    /**
     * Checks if a translation exists for a key
     * @param key The key to check
     * @returns True if the translation exists, false otherwise
     */
    translationExists(key: string): boolean {
        try {
            const translation = this.translocoService.translate(key);
            // If the translation is the same as the key, it likely doesn't exist
            // This is a common pattern in i18n libraries when a translation is missing
            return translation !== key && translation !== undefined && translation !== null;
        } catch {
            return false;
        }
    }

    /**
     * Loads translations for a specific language
     * @param lang The language to load
     * @returns An observable that completes when the language is loaded
     */
    load(lang: string): Observable<any> {
        return this.translocoService.load(lang);
    }
}
